/**
 * SlidePanel — Animated slide-in drawer from the right edge.
 *
 * Supports:
 *   - Smooth open/close translateX animation (0.3s ease)
 *   - Internal navigation stack (push/pop sub-screens)
 *   - Configurable width (sm, md, lg, xl, full)
 *   - Backdrop blur + click-to-close
 *   - Accessible focus trap
 *
 * Inspired by rule42-cloud cockpit drawer (WorkspaceHomePage ATP panel).
 *
 * @module packages/billing-admin/src/components/SlidePanel
 * @since 2026-06-08
 */
import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PanelSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface SlidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Panel width */
  size?: PanelSize;
  /** Panel title (shown in header) */
  title?: string;
  /** Subtitle (shown under title) */
  subtitle?: string;
  /** Category label (shown above title) */
  category?: string;
  /** Content */
  children: React.ReactNode;
  /** Footer content */
  footer?: React.ReactNode;
  /** Whether to show the back button (for nested navigation) */
  showBack?: boolean;
  /** Back button callback */
  onBack?: () => void;
  /** Hide the default header */
  hideHeader?: boolean;
}

const SIZE_MAP: Record<PanelSize, string> = {
  sm: 'w-[380px] max-w-[85vw]',
  md: 'w-[520px] max-w-[90vw]',
  lg: 'w-[640px] max-w-[92vw]',
  xl: 'w-[800px] max-w-[95vw]',
  full: 'w-[95vw]',
};

// ---------------------------------------------------------------------------
// Panel Navigation Context (for stacked sub-screens)
// ---------------------------------------------------------------------------

export interface PanelScreen {
  id: string;
  title: string;
  subtitle?: string;
  category?: string;
  content: React.ReactNode;
  footer?: React.ReactNode;
}

interface PanelNavContextValue {
  push: (screen: PanelScreen) => void;
  pop: () => void;
  replace: (screen: PanelScreen) => void;
  depth: number;
}

const PanelNavContext = createContext<PanelNavContextValue>({
  push: () => {},
  pop: () => {},
  replace: () => {},
  depth: 0,
});

export function usePanelNav(): PanelNavContextValue {
  return useContext(PanelNavContext);
}

// ---------------------------------------------------------------------------
// SlidePanel Component
// ---------------------------------------------------------------------------

export default function SlidePanel({
  open,
  onClose,
  size = 'md',
  title,
  subtitle,
  category,
  children,
  footer,
  showBack,
  onBack,
  hideHeader,
}: SlidePanelProps): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [navStack, setNavStack] = useState<PanelScreen[]>([]);
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('left');
  const panelRef = useRef<HTMLDivElement>(null);

  // Mount → animate in
  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => {
        setMounted(false);
        setNavStack([]);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!mounted) return;
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        if (navStack.length > 0) {
          popScreen();
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mounted, navStack.length, onClose]);

  // Navigation methods
  const pushScreen = useCallback((screen: PanelScreen) => {
    setSlideDir('left');
    setNavStack((prev) => [...prev, screen]);
  }, []);

  const popScreen = useCallback(() => {
    setSlideDir('right');
    setNavStack((prev) => prev.slice(0, -1));
  }, []);

  const replaceScreen = useCallback((screen: PanelScreen) => {
    setSlideDir('left');
    setNavStack((prev) => [...prev.slice(0, -1), screen]);
  }, []);

  if (!mounted) return null;

  const currentScreen = navStack.length > 0 ? navStack[navStack.length - 1] : null;
  const displayTitle = currentScreen?.title ?? title;
  const displaySubtitle = currentScreen?.subtitle ?? subtitle;
  const displayCategory = currentScreen?.category ?? category;
  const displayContent = currentScreen?.content ?? children;
  const displayFooter = currentScreen?.footer ?? footer;
  const canGoBack = navStack.length > 0 || showBack;

  const panel = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => {
          if (navStack.length > 0) popScreen();
          else onClose();
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed inset-y-0 right-0 z-[9999] flex flex-col bg-white shadow-2xl border-l border-slate-200/80 ${SIZE_MAP[size]} transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        {!hideHeader && (
          <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-4">
            {canGoBack && (
              <button
                onClick={() => {
                  if (navStack.length > 0) popScreen();
                  else onBack?.();
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
            )}
            <div className="flex-1 min-w-0">
              {displayCategory && (
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-cyan-700">{displayCategory}</p>
              )}
              {displayTitle && (
                <h2 className="text-lg font-semibold text-slate-900 truncate">{displayTitle}</h2>
              )}
              {displaySubtitle && (
                <p className="text-sm text-slate-500 truncate">{displaySubtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content with internal slide animation */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div
            key={navStack.map((s) => s.id).join('/')}
            className={`h-full animate-panel-${slideDir}`}
          >
            <PanelNavContext.Provider value={{ push: pushScreen, pop: popScreen, replace: replaceScreen, depth: navStack.length }}>
              {displayContent}
            </PanelNavContext.Provider>
          </div>
        </div>

        {/* Footer */}
        {displayFooter && (
          <div className="border-t border-slate-200 px-6 py-4">
            {displayFooter}
          </div>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes panelSlideLeft {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes panelSlideRight {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-panel-left { animation: panelSlideLeft 0.25s ease-out; }
        .animate-panel-right { animation: panelSlideRight 0.25s ease-out; }
      `}</style>
    </>
  );

  return createPortal(panel, document.body);
}
